import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { MemberType } from '@signals/app-shell/services/intelPacketApi';

export type WatchScope = 'aoi' | 'global';
export type LookbackDays = 30 | 60 | 90 | 365;

export interface AddMemberDraft {
  member_type: MemberType;
  member_id: string;
  label: string;
  notes: string;
  scope: WatchScope;
  lookback_days: LookbackDays;
}

interface AddMemberDialogProps {
  open: boolean;
  draft: AddMemberDraft | null;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (draft: AddMemberDraft) => void;
}

const AddMemberDialog: React.FC<AddMemberDialogProps> = ({ open, draft, busy, onClose, onConfirm }) => {
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [scope, setScope] = useState<WatchScope>('global');
  const [lookback, setLookback] = useState<LookbackDays>(30);
  const isAdtech = draft?.member_type === 'adtech_id';

  useEffect(() => {
    if (!draft) return;
    setLabel(draft.label || draft.member_id);
    setNotes(draft.notes || '');
    setScope(draft.scope || 'global');
    setLookback(draft.lookback_days || 30);
  }, [draft]);

  if (!draft) return null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add to packet</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, wordBreak: 'break-all' }}>
          {draft.member_type} · {draft.member_id}
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="Name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Notes / descriptor"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Watch scope</Typography>
        <RadioGroup
          value={scope}
          onChange={(_, v) => setScope(v as WatchScope)}
        >
          <FormControlLabel value="aoi" control={<Radio size="small" />} label="Only inside the packet geofence" />
          <FormControlLabel value="global" control={<Radio size="small" />} label="Wherever this ID is seen" />
        </RadioGroup>
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel>Historic PoL window</InputLabel>
          <Select
            label="Historic PoL window"
            value={lookback}
            onChange={(e) => setLookback(Number(e.target.value) as LookbackDays)}
          >
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={365} disabled={isAdtech}>Last 365 days{isAdtech ? ' (AdTech cap 90)' : ''}</MenuItem>
          </Select>
        </FormControl>
        {isAdtech && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            AdTech vendor history is capped at 90 days.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!!busy || !label.trim()}
          onClick={() => onConfirm({
            ...draft,
            label: label.trim(),
            notes: notes.trim(),
            scope,
            lookback_days: isAdtech && lookback > 90 ? 90 : lookback,
          })}
        >
          {busy ? 'Adding…' : 'Add and run historic PoL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberDialog;
